import React from "react";
import type { HistoryItem } from "../HistoryPanel/HistoryPanel";

/** One undo step: a label/timestamp for the history panel plus the full
 *  point-in-time snapshot to restore. The snapshot shape is owned by the
 *  caller (the grid's editable state), so the hook is generic over it. */
export type HistoryEntry<Snap> = { id: number; label: string; at: number; snap: Snap };

interface UndoArgs<Snap> {
  /** The current editable-state snapshot, kept fresh by the caller each render.
   *  pushUndo reads it without a dependency-array churn. */
  snapStateRef: React.MutableRefObject<Snap | null>;
  /** Applies a captured snapshot back onto the live state. */
  restoreSnap: (snap: Snap) => void;
  /** When true, pushUndo records nothing (a programmatic edit not meant to be
   *  an undo step). */
  squelchUndoRef: React.MutableRefObject<boolean>;
  /** Flipped true on any push — gates the draft load so a late fetch can't
   *  clobber freshly-staged work. */
  userEditedRef: React.MutableRefObject<boolean>;
}

interface UndoApi {
  historyItems: HistoryItem[];
  undoCount: number;
  pushUndo: (label?: string) => void;
  undo: () => void;
  jumpToHistory: (id: number) => void;
  /** Clears all history (used when the draft is reset or a saved draft loads). */
  resetHistory: () => void;
}

export function useUndo<Snap>({
  snapStateRef,
  restoreSnap,
  squelchUndoRef,
  userEditedRef,
}: UndoArgs<Snap>): UndoApi {
  const undoRef = React.useRef<HistoryEntry<Snap>[]>([]);
  const historySeqRef = React.useRef(0);
  const [historyItems, setHistoryItems] = React.useState<HistoryItem[]>([]);
  const undoCount = historyItems.length;

  const pushUndo = React.useCallback(
    (label: string = "Edit") => {
      userEditedRef.current = true;
      if (squelchUndoRef.current) return;
      if (!snapStateRef.current) return;
      const s = undoRef.current;
      s.push({
        id: ++historySeqRef.current,
        label,
        at: Date.now(),
        snap: snapStateRef.current,
      });
      if (s.length > 50) s.shift();
      setHistoryItems(s.map((e) => ({ id: e.id, label: e.label, at: e.at })).reverse());
    },
    [snapStateRef, squelchUndoRef, userEditedRef],
  );

  const undo = React.useCallback(() => {
    const entry = undoRef.current.pop();
    setHistoryItems(
      undoRef.current.map((e) => ({ id: e.id, label: e.label, at: e.at })).reverse(),
    );
    if (entry) restoreSnap(entry.snap);
  }, [restoreSnap]);

  const jumpToHistory = React.useCallback(
    (id: number) => {
      const s = undoRef.current;
      const idx = s.findIndex((e) => e.id === id);
      if (idx < 0) return;
      const entry = s[idx];
      s.length = idx; // drop this entry and everything after it
      setHistoryItems(s.map((e) => ({ id: e.id, label: e.label, at: e.at })).reverse());
      restoreSnap(entry.snap);
    },
    [restoreSnap],
  );

  const resetHistory = React.useCallback(() => {
    undoRef.current = [];
    setHistoryItems([]);
  }, []);

  return { historyItems, undoCount, pushUndo, undo, jumpToHistory, resetHistory };
}
